/**
 * Renders category-specific detail form fields for an item.
 *
 * Each item category (except TOOL) has a dedicated section component:
 * BoardGameFields, PuzzleFields, BookFields, ToyFields, EventPartyFields.
 * The `CATEGORY_FORM_COMPONENT` dispatch table maps categories to their
 * section component so the main export (`CategoryDetailsFields`) stays thin.
 *
 * TOOL returns null because tools have no extra required fields beyond the
 * base item form (name, description, brand, condition).
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { ItemCategory } from '@/lib/itemCategoryConfig';
import {
  BOARD_GAME_TYPE_OPTIONS,
  BOOK_FORMAT_OPTIONS,
  BOOK_GENRE_OPTIONS,
  EVENT_PARTY_TYPE_OPTIONS,
  POWER_TYPE_OPTIONS,
  PUZZLE_THEME_OPTIONS,
  PUZZLE_TYPE_OPTIONS,
  TOY_TYPE_OPTIONS,
  TRANSPORT_SIZE_OPTIONS,
  type CategoryDetailsState,
} from '@/lib/itemCategoryDetails';

interface Props {
  category: ItemCategory;
  details: CategoryDetailsState;
  onChange: (details: CategoryDetailsState) => void;
  idPrefix: string;
}

interface SectionProps {
  details: CategoryDetailsState;
  onChange: (details: CategoryDetailsState) => void;
  idPrefix: string;
}

/**
 * Produces a new `CategoryDetailsState` with a partial update applied to one
 * category's sub-object. Keeps all other category states untouched.
 * Each section component creates a local alias of this (e.g. `updateBoardGame`)
 * that pre-fills the `key` argument for convenience.
 * @param details
 * @param key
 * @param patch
 */
// K is constrained to valid category keys in CategoryDetailsState
const updateDetails = <K extends keyof CategoryDetailsState>(
  details: CategoryDetailsState,
  key: K,
  patch: Partial<CategoryDetailsState[K]>,
): CategoryDetailsState => ({ ...details, [key]: { ...details[key], ...patch } });

// Local UI helper components used by all section components.
// Defined here (not in components/ui/) as they are specific to this form.
/**
 *
 * @param root0
 * @param root0.children
 */
function SectionLabel({ children }: { children: string }) {
  return <p className="text-sm font-medium">{children}</p>;
}

/**
 *
 * @param root0
 * @param root0.id
 * @param root0.label
 * @param root0.checked
 * @param root0.onCheckedChange
 */
function SwitchRow({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id}>{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
function BoardGameFields({ details, onChange, idPrefix }: SectionProps) {
  const updateBoardGame = (patch: Partial<CategoryDetailsState['boardGame']>) =>
    onChange(updateDetails(details, 'boardGame', patch));

  return (
    <div className="space-y-4">
      <SectionLabel>Board Game Details</SectionLabel>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}gameType`}>Game Type *</Label>
        <Select
          value={details.boardGame.gameType}
          onValueChange={(value) =>
            updateBoardGame({ gameType: value as typeof details.boardGame.gameType })
          }
        >
          <SelectTrigger id={`${idPrefix}gameType`}>
            <SelectValue placeholder="Select game type" />
          </SelectTrigger>
          <SelectContent>
            {BOARD_GAME_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}minPlayers`}>Min Players *</Label>
          <Input
            id={`${idPrefix}minPlayers`}
            type="number"
            min={1}
            value={details.boardGame.minPlayers}
            onChange={(e) => updateBoardGame({ minPlayers: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}maxPlayers`}>Max Players *</Label>
          <Input
            id={`${idPrefix}maxPlayers`}
            type="number"
            min={1}
            value={details.boardGame.maxPlayers}
            onChange={(e) => updateBoardGame({ maxPlayers: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}playTimeMinutes`}>Play Time (minutes) *</Label>
          <Input
            id={`${idPrefix}playTimeMinutes`}
            type="number"
            min={1}
            value={details.boardGame.playTimeMinutes}
            onChange={(e) => updateBoardGame({ playTimeMinutes: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}recommendedAge`}>Recommended Age (optional)</Label>
          <Input
            id={`${idPrefix}recommendedAge`}
            type="number"
            min={0}
            value={details.boardGame.recommendedAge}
            onChange={(e) => updateBoardGame({ recommendedAge: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}publisher`}>Publisher (optional)</Label>
        <Input
          id={`${idPrefix}publisher`}
          value={details.boardGame.publisher}
          onChange={(e) => updateBoardGame({ publisher: e.target.value })}
        />
      </div>
      <SwitchRow
        id={`${idPrefix}missingComponents`}
        label="Missing Components"
        checked={details.boardGame.missingComponents}
        onCheckedChange={(value) => updateBoardGame({ missingComponents: value })}
      />
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}missingComponentsNotes`}>
          Missing Components Notes (optional)
        </Label>
        <Input
          id={`${idPrefix}missingComponentsNotes`}
          value={details.boardGame.missingComponentsNotes}
          onChange={(e) => updateBoardGame({ missingComponentsNotes: e.target.value })}
        />
      </div>
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
function PuzzleFields({ details, onChange, idPrefix }: SectionProps) {
  const updatePuzzle = (patch: Partial<CategoryDetailsState['puzzle']>) =>
    onChange(updateDetails(details, 'puzzle', patch));

  return (
    <div className="space-y-4">
      <SectionLabel>Puzzle Details</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}puzzleType`}>Puzzle Type *</Label>
          <Select
            value={details.puzzle.puzzleType}
            onValueChange={(value) =>
              updatePuzzle({ puzzleType: value as typeof details.puzzle.puzzleType })
            }
          >
            <SelectTrigger id={`${idPrefix}puzzleType`}>
              <SelectValue placeholder="Select puzzle type" />
            </SelectTrigger>
            <SelectContent>
              {PUZZLE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}pieceCount`}>Piece Count *</Label>
          <Input
            id={`${idPrefix}pieceCount`}
            type="number"
            min={1}
            value={details.puzzle.pieceCount}
            onChange={(e) => updatePuzzle({ pieceCount: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}puzzleAge`}>Recommended Age (optional)</Label>
          <Input
            id={`${idPrefix}puzzleAge`}
            type="number"
            min={0}
            value={details.puzzle.recommendedAge}
            onChange={(e) => updatePuzzle({ recommendedAge: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}puzzleTheme`}>Theme *</Label>
          <Select
            value={details.puzzle.theme}
            onValueChange={(value) => updatePuzzle({ theme: value as typeof details.puzzle.theme })}
          >
            <SelectTrigger id={`${idPrefix}puzzleTheme`}>
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              {PUZZLE_THEME_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <SwitchRow
        id={`${idPrefix}complete`}
        label="Complete"
        checked={details.puzzle.complete}
        onCheckedChange={(value) => updatePuzzle({ complete: value })}
      />
      <SwitchRow
        id={`${idPrefix}originalBox`}
        label="Has Original Box"
        checked={details.puzzle.hasOriginalBox}
        onCheckedChange={(value) => updatePuzzle({ hasOriginalBox: value })}
      />
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
function BookFields({ details, onChange, idPrefix }: SectionProps) {
  const updateBook = (patch: Partial<CategoryDetailsState['book']>) =>
    onChange(updateDetails(details, 'book', patch));

  return (
    <div className="space-y-4">
      <SectionLabel>Book Details</SectionLabel>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}author`}>Author *</Label>
        <Input
          id={`${idPrefix}author`}
          value={details.book.author}
          onChange={(e) => updateBook({ author: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}format`}>Format *</Label>
          <Select
            value={details.book.format}
            onValueChange={(value) => updateBook({ format: value as typeof details.book.format })}
          >
            <SelectTrigger id={`${idPrefix}format`}>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              {BOOK_FORMAT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}genre`}>Genre *</Label>
          <Select
            value={details.book.genre}
            onValueChange={(value) => updateBook({ genre: value as typeof details.book.genre })}
          >
            <SelectTrigger id={`${idPrefix}genre`}>
              <SelectValue placeholder="Select genre" />
            </SelectTrigger>
            <SelectContent>
              {BOOK_GENRE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}isbn13`}>ISBN13 (optional)</Label>
        <Input
          id={`${idPrefix}isbn13`}
          value={details.book.isbn13}
          onChange={(e) => updateBook({ isbn13: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}language`}>Language (optional)</Label>
          <Input
            id={`${idPrefix}language`}
            value={details.book.language}
            onChange={(e) => updateBook({ language: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}seriesName`}>Series Name (optional)</Label>
          <Input
            id={`${idPrefix}seriesName`}
            value={details.book.seriesName}
            onChange={(e) => updateBook({ seriesName: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}seriesNumber`}>Series # (optional)</Label>
          <Input
            id={`${idPrefix}seriesNumber`}
            type="number"
            min={1}
            value={details.book.seriesNumber}
            onChange={(e) => updateBook({ seriesNumber: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}recommendedAge`}>Recommended Age (optional)</Label>
          <Input
            id={`${idPrefix}recommendedAge`}
            type="number"
            min={0}
            value={details.book.recommendedAge}
            onChange={(e) => updateBook({ recommendedAge: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
function ToyFields({ details, onChange, idPrefix }: SectionProps) {
  const updateToy = (patch: Partial<CategoryDetailsState['toy']>) =>
    onChange(updateDetails(details, 'toy', patch));

  return (
    <div className="space-y-4">
      <SectionLabel>Toy Details</SectionLabel>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}toyType`}>Toy Type *</Label>
        <Select
          value={details.toy.toyType}
          onValueChange={(value) => updateToy({ toyType: value as typeof details.toy.toyType })}
        >
          <SelectTrigger id={`${idPrefix}toyType`}>
            <SelectValue placeholder="Select toy type" />
          </SelectTrigger>
          <SelectContent>
            {TOY_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}toyBrand`}>Toy Brand (optional)</Label>
        <Input
          id={`${idPrefix}toyBrand`}
          value={details.toy.brand}
          onChange={(e) => updateToy({ brand: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}toyRecommendedAge`}>Recommended Age (optional)</Label>
        <Input
          id={`${idPrefix}toyRecommendedAge`}
          type="number"
          min={0}
          value={details.toy.recommendedAge}
          onChange={(e) => updateToy({ recommendedAge: e.target.value })}
        />
      </div>
      <SwitchRow
        id={`${idPrefix}smallParts`}
        label="Contains Small Parts"
        checked={details.toy.containsSmallParts}
        onCheckedChange={(value) => updateToy({ containsSmallParts: value })}
      />
      <SwitchRow
        id={`${idPrefix}batteryRequired`}
        label="Battery Required"
        checked={details.toy.batteryRequired}
        onCheckedChange={(value) => updateToy({ batteryRequired: value })}
      />
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
function EventPartyFields({ details, onChange, idPrefix }: SectionProps) {
  const updateEventParty = (patch: Partial<CategoryDetailsState['eventParty']>) =>
    onChange(updateDetails(details, 'eventParty', patch));

  return (
    <div className="space-y-4">
      <SectionLabel>Event & Party Details</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}eventType`}>Event Type *</Label>
          <Select
            value={details.eventParty.eventType}
            onValueChange={(value) =>
              updateEventParty({ eventType: value as typeof details.eventParty.eventType })
            }
          >
            <SelectTrigger id={`${idPrefix}eventType`}>
              <SelectValue placeholder="Select event type" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_PARTY_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}transport`}>Transport Size *</Label>
          <Select
            value={details.eventParty.transportSize}
            onValueChange={(value) =>
              updateEventParty({ transportSize: value as typeof details.eventParty.transportSize })
            }
          >
            <SelectTrigger id={`${idPrefix}transport`}>
              <SelectValue placeholder="Select transport size" />
            </SelectTrigger>
            <SelectContent>
              {TRANSPORT_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <SwitchRow
        id={`${idPrefix}powerRequired`}
        label="Power Required"
        checked={details.eventParty.powerRequired}
        onCheckedChange={(value) => {
          // Keep power type coherent with whether power is needed.
          updateEventParty({
            powerRequired: value,
            powerType: value ? details.eventParty.powerType : 'NONE',
          });
        }}
      />
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}powerType`}>Power Type *</Label>
        <Select
          value={details.eventParty.powerType}
          onValueChange={(value) =>
            updateEventParty({ powerType: value as typeof details.eventParty.powerType })
          }
        >
          <SelectTrigger id={`${idPrefix}powerType`}>
            <SelectValue placeholder="Select power type" />
          </SelectTrigger>
          <SelectContent>
            {POWER_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}accessories`}>Accessories Included (optional)</Label>
        <Input
          id={`${idPrefix}accessories`}
          value={details.eventParty.accessoriesIncluded}
          onChange={(e) => updateEventParty({ accessoriesIncluded: e.target.value })}
        />
      </div>
    </div>
  );
}

// Dispatch table: maps each category to its section component.
// Adding a new category only requires adding an entry here + a new component.
const CATEGORY_FORM_COMPONENT: Record<
  Exclude<ItemCategory, 'TOOL'>,
  (props: SectionProps) => JSX.Element
> = {
  BOARD_GAME: BoardGameFields,
  PUZZLE: PuzzleFields,
  BOOK: BookFields,
  TOY: ToyFields,
  EVENT_PARTY: EventPartyFields,
};

/**
 *
 * @param root0
 * @param root0.category
 * @param root0.details
 * @param root0.onChange
 * @param root0.idPrefix
 */
export function CategoryDetailsFields({ category, details, onChange, idPrefix }: Props) {
  // TOOL has no extra required fields — return early to render nothing.
  if (category === 'TOOL') return null;

  const Section = CATEGORY_FORM_COMPONENT[category];
  return <Section details={details} onChange={onChange} idPrefix={idPrefix} />;
}
